import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { printViaIframe } from '../../lib/printHelper';
import { getStandDisplayValues, printStandPoster } from '../../lib/conventionStandPoster';
import { getStandMagnumFacts, buildStandScriptLines, formatStandMagnumSummary } from '../../lib/conventionCourse';
import { isConventionMember, isConventionOrganizer } from '../../lib/conventionResults';

// Standard NSF feltpistol range commands read aloud by the standplassleder.
// Static club script (from Dokka Pistolklubb's standplass sheets) — the
// stand-specific lines (position, time, targets, zones) are injected between
// the intro and the questions line. Read-only module constant; never mutate.
const SCRIPT_INTRO = [
    { text: 'LES OPP SKYTTERS NAVN OG SKIVENR.' },
    { text: 'TA PÅ BRILLER/HØRSELVERN.' },
    { text: 'DERE KAN TA FREM VÅPEN OG FYLLE TØNNE/MAGASIN MED RETT ANTALL SKUDD MENS JEG FORKLARER ØVELSEN. Standplassleder skal kontrollere at det ikke lades med for mange skudd.' },
    { text: 'MAGNUM FYLLER OGSÅ MEN VENTER PÅ EGEN LADE-KOMMANDO.' },
];

const SCRIPT_COMMANDS = [
    { cmd: 'LADING:', text: 'Skytterne lader våpen, etter 1 minutt kommer kommandoen:' },
    { cmd: 'ER SKYTTERNE KLARE:', text: 'Skytterne får 5 sekunder til å melde fra om de ikke er klar. Dersom en eller flere skyttere melder fra, skal det gis 15 sekunder ventetid, deretter spør skyteleder om skytterne er klar, men det gis ikke ytterligere ventetid.' },
    { cmd: 'KLAR:', text: 'I løpet av de neste 5 sekundene skal våpen senkes ned i 45 grader. I løpet av perioden «klar» er det ikke tillatt å heve våpenet, det skal kun senkes.' },
    { cmd: 'ILD:', text: 'På denne kommandoen heves våpen og skudd avgis.' },
    { cmd: 'STANS:', text: 'Ropes de 2 siste sekundene av skytetiden.' },
    { cmd: 'TØM VÅPEN, GJØR KLAR TIL VISITASJON:', text: 'Skytterne tømmer og plugger våpen og gjør klar til visitasjon. Skyteleder inspiserer alle våpen og magasin. PAKK NED VÅPEN ELLER HYLSTRE.' },
    { cmd: 'ANVIS:', text: 'Skiver og mål anvises, og resultat registreres og leses opp for skytterne før de forlater standplass. Etter at resultater er godtatt av skytter lappes skive.' },
];

const escapeHtml = (s = '') => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// Numeric stageStands fields. Cleared inputs save as 0, which the app treats
// as "not set" (getStandValues/getStandInfo only accept positive integers).
const NUMERIC_STAND_FIELDS = ['shootingTime', 'magnumShootingTime', 'scoreHits', 'scoreTargets', 'scoreGroups', 'scoreInner'];

@observer
class ConventionStandplassCard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            editing: false,
            creatingEntry: false,
            editObject: {},
            updateInProgress: {},
        };
        this.updateTimers = {};
    }

    componentDidMount() {
        this._mounted = true;
    }

    componentWillUnmount() {
        // Pending debounce timers keep running so a just-typed value still saves;
        // doAutoSave guards its setState with _mounted.
        this._mounted = false;
    }
    // The raw stageStands entry for this stand — scoreHits (maks treff),
    // scoreTargets (mål), scoreGroups (målgrupper), scoreInner (soner),
    // shootingTime, shootingPosition and notes are all optional.
    getStandEntry = () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const standNum = parseInt(this.props.stand, 10) || 1;
        const stageStands = convention?.stageStands || [];
        return stageStands.find(item => parseInt(item.stand, 10) === standNum) || {};
    }

    // Same official check as the scoring page: everyone on the convention's
    // "Medlemmer" or "Arrangører" list can maintain stand info, not only site
    // admins.
    isConventionAdmin = () => {
        const { userStore, conventionStore } = this.props.stores;
        const { user = {}, isAdmin, isCustomerAdmin } = userStore;
        const convention = conventionStore.convention || {};
        return isAdmin
            || isCustomerAdmin
            || isConventionMember(convention, user)
            || isConventionOrganizer(convention, user)
            || (!!user.id && convention.user === user.id);
    }

    toggleEdit = async () => {
        if (this.state.editing) {
            this.setState({ editing: false });
            return;
        }
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const standNum = parseInt(this.props.stand, 10) || 1;
        let entry = (convention?.stageStands || []).find(item => parseInt(item.stand, 10) === standNum);
        // A stand with no config yet gets its entry created up front, so every
        // subsequent auto-save can address it by the md5 addArrayObject stamps on it.
        if (!entry && convention?.id) {
            this.setState({ creatingEntry: true });
            await conventionStore.addArrayObject({
                newObject: { stand: standNum },
                object: convention,
                field: 'stageStands',
                objectName: 'convention',
            });
            entry = (conventionStore.convention?.stageStands || []).find(item => parseInt(item.stand, 10) === standNum);
            if (!this._mounted) return;
        }
        entry = entry || {};
        this.setState({
            creatingEntry: false,
            editing: true,
            editObject: {
                shootingTime: entry.shootingTime || '',
                shootingPosition: entry.shootingPosition || '',
                magnumShootingTime: entry.magnumShootingTime || '',
                magnumShootingPosition: entry.magnumShootingPosition || '',
                magnumNotes: entry.magnumNotes || '',
                scoreTargets: entry.scoreTargets || '',
                scoreGroups: entry.scoreGroups || '',
                scoreInner: entry.scoreInner || '',
                scoreHits: entry.scoreHits || '',
                notes: entry.notes || '',
            },
        });
    }

    handleFieldChange = (e) => {
        const { name, value } = e.target;
        this.setState(prevState => ({
            editObject: { ...prevState.editObject, [name]: value },
            updateInProgress: { ...prevState.updateInProgress, [name]: true },
        }));
        if (this.updateTimers[name]) {
            clearTimeout(this.updateTimers[name]);
        }
        const capturedName = name;
        const capturedValue = value;
        this.updateTimers[name] = setTimeout(() => {
            this.doAutoSave({ name: capturedName, value: capturedValue });
        }, 750);
    }

    doAutoSave = async ({ name, value }) => {
        const { conventionStore, appState } = this.props.stores;
        const { convention } = conventionStore;
        const standNum = parseInt(this.props.stand, 10) || 1;
        const entry = (convention?.stageStands || []).find(item => parseInt(item.stand, 10) === standNum);
        const isNumeric = NUMERIC_STAND_FIELDS.includes(name);
        const saveValue = isNumeric ? (parseInt(value, 10) || 0) : value;

        let ok = false;
        if (convention?.id && entry && (entry.md5 || entry.uuidv4)) {
            const response = await conventionStore.editArrayObject({
                field: 'stageStands',
                key: name,
                value: saveValue,
                type: isNumeric ? 'number' : undefined,
                id: convention.id,
                md5: entry.md5 || undefined,
                uuidv4: entry.md5 ? undefined : entry.uuidv4,
                objectName: 'convention',
            });
            ok = !!response && response.status === 202;
        } else if (convention?.id && entry) {
            // Entries imported/copied without md5/uuidv4 can't be addressed by
            // editArrayObject — overwrite the array with a COPY holding the change.
            const newStands = (convention.stageStands || []).map(item => (
                parseInt(item.stand, 10) === standNum ? { ...item, [name]: saveValue } : item
            ));
            const response = await conventionStore.save({ id: convention.id, 'stageStands-overwrite': newStands });
            ok = !!response && response.status >= 200 && response.status < 300;
            if (ok) {
                conventionStore.updateObjectKeyValue('convention', 'stageStands', newStands);
            }
        }
        if (this._mounted) {
            this.setState(prevState => ({
                updateInProgress: { ...prevState.updateInProgress, [name]: false },
            }));
        }
        if (!ok && appState && appState.addToast) {
            appState.addToast({ type: 'error', message: 'Kunne ikke lagre standplass-endringen' }, 5000);
        }
    }

    getStandValues = () => getStandDisplayValues(this.getStandEntry());

    // The stand-specific script lines shared by the on-screen view and the
    // printed instruction card (see buildStandScriptLines).
    getExerciseLines = () => {
        const entry = this.getStandEntry();
        return buildStandScriptLines(getStandDisplayValues(entry), getStandMagnumFacts(entry));
    }

    printCard = () => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const standNum = parseInt(this.props.stand, 10) || 1;
        const exerciseLines = this.getExerciseLines();

        const introHtml = SCRIPT_INTRO.map(item => `<li>${escapeHtml(item.text)}</li>`).join('');
        const exerciseHtml = exerciseLines.map(line => `<li class="exercise">${escapeHtml(line)}</li>`).join('');
        const commandsHtml = SCRIPT_COMMANDS.map(item => `<li><span class="cmd">${escapeHtml(item.cmd)}</span> ${escapeHtml(item.text)}</li>`).join('');

        const html = `
            <div class="print-section standplass-card">
                <div class="card-convention">${escapeHtml(convention?.title || '')}</div>
                <h1>VELKOMMEN TIL STANDPLASS NR. ${standNum}</h1>
                <ul>
                    ${introHtml}
                    ${exerciseHtml}
                    <li class="exercise">ER DET NOEN SPØRSMÅL TIL ØVELSEN?</li>
                    ${commandsHtml}
                </ul>
            </div>
        `;
        const customCss = `
            .standplass-card { font-family: Arial, Helvetica, sans-serif; color: #000; }
            .standplass-card .card-convention { font-size: 11pt; color: #444; margin-bottom: 2mm; }
            .standplass-card h1 { font-size: 18pt; font-weight: 700; margin: 0 0 6mm 0; text-decoration: underline; }
            .standplass-card ul { margin: 0; padding-left: 6mm; }
            .standplass-card li { font-size: 12pt; line-height: 1.35; margin-bottom: 4mm; }
            .standplass-card li.exercise { font-weight: 700; font-size: 13pt; }
            .standplass-card li .cmd { font-weight: 700; text-decoration: underline; }
            @media print {
                @page { size: A4 portrait; margin: 15mm; }
            }
        `;
        printViaIframe(html, { customCss });
    }

    printPoster = () => {
        const { conventionStore } = this.props.stores;
        printStandPoster({ convention: conventionStore.convention, stand: this.props.stand });
    }

    render() {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const standNum = parseInt(this.props.stand, 10) || 1;
        const { shootingTime, shootingPosition, notes, scoreHits, scoreTargets, scoreGroups, scoreInner } = this.getStandValues();
        const standImage = convention?.stageStandImages
            ? convention.stageStandImages[standNum - 1]
            : null;
        const exerciseLines = this.getExerciseLines();
        const magnumFacts = getStandMagnumFacts(this.getStandEntry());
        const { editing, creatingEntry, editObject, updateInProgress } = this.state;
        const isConventionAdmin = this.isConventionAdmin();
        const anySaving = Object.values(updateInProgress).some(v => v);

        return (
            <div class='h-100 d-flex flex-column bg-body text-body' style='padding-top: 3.25rem;'>
                <div class='flex-grow-1 overflow-auto px-3 pb-4'>
                    <div class='text-center mb-3'>
                        <div class='text-body-secondary'>{convention?.title}</div>
                        <h4 class='mb-0'>
                            <Text id='convention.standplass-card-title'>Stand instruction card</Text> — <Text id='convention.stand'>Stand</Text> {standNum}
                        </h4>
                    </div>

                    <div class='d-flex flex-wrap justify-content-center gap-2 mb-3'>
                        <button
                            type='button'
                            class='btn btn-primary rounded-pill px-4'
                            onClick={this.printCard}
                        >
                            <i class='fa-duotone fa-print me-2' />
                            <Text id='convention.standplass-card-print'>Print instruction card</Text>
                        </button>
                        <button
                            type='button'
                            class='btn btn-outline-primary rounded-pill px-4'
                            onClick={this.printPoster}
                        >
                            <i class='fa-duotone fa-sign-hanging me-2' />
                            <Text id='convention.standplass-poster-print'>Print poster</Text>
                        </button>
                        {isConventionAdmin && (
                            <button
                                type='button'
                                class={`btn rounded-pill px-4 ${editing ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                onClick={this.toggleEdit}
                                disabled={creatingEntry}
                            >
                                {creatingEntry
                                    ? <i class='fa-duotone fa-spinner-third fa-spin me-2' />
                                    : <i class='fa-duotone fa-pen-to-square me-2' />}
                                {editing
                                    ? <Text id='convention.standplass-edit-done'>Done</Text>
                                    : <Text id='convention.standplass-edit'>Edit stand</Text>}
                            </button>
                        )}
                    </div>

                    {/* Admin edit panel — auto-saves each field (750ms debounce) */}
                    {editing && isConventionAdmin && (
                        <div class='card mb-3'>
                            <div class='card-body'>
                                <div class='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
                                    <h6 class='mb-0'>
                                        <i class='fa-duotone fa-pen-to-square me-2' />
                                        <Text id='convention.standplass-edit-title'>Edit stand info</Text>
                                    </h6>
                                    {anySaving ? (
                                        <small class='text-body-secondary'>
                                            <i class='fa-duotone fa-spinner-third fa-spin me-1' />
                                            <Text id='convention.standplass-edit-saving'>Saving ...</Text>
                                        </small>
                                    ) : (
                                        <small class='text-body-secondary'>
                                            <i class='fa-duotone fa-circle-check me-1' />
                                            <Text id='convention.standplass-edit-autosave'>Changes are saved automatically</Text>
                                        </small>
                                    )}
                                </div>
                                <div class='row g-2'>
                                    <div class='col-6 col-sm-4'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-shooting-time'>Shooting time (sec)</Text></label>
                                        <input
                                            type='number'
                                            min='0'
                                            class='form-control'
                                            name='shootingTime'
                                            value={editObject.shootingTime}
                                            onInput={this.handleFieldChange}
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div class='col-6 col-sm-4'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-figures'>Figures</Text></label>
                                        <input
                                            type='number'
                                            min='0'
                                            class='form-control'
                                            name='scoreTargets'
                                            value={editObject.scoreTargets}
                                            onInput={this.handleFieldChange}
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div class='col-6 col-sm-4'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-groups'>Target groups</Text></label>
                                        <input
                                            type='number'
                                            min='0'
                                            class='form-control'
                                            name='scoreGroups'
                                            value={editObject.scoreGroups}
                                            onInput={this.handleFieldChange}
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div class='col-6 col-sm-4'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-zones'>Zones</Text></label>
                                        <input
                                            type='number'
                                            min='0'
                                            class='form-control'
                                            name='scoreInner'
                                            value={editObject.scoreInner}
                                            onInput={this.handleFieldChange}
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div class='col-6 col-sm-4'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-max-hits'>Max hits</Text></label>
                                        <input
                                            type='number'
                                            min='0'
                                            class='form-control'
                                            name='scoreHits'
                                            value={editObject.scoreHits}
                                            onInput={this.handleFieldChange}
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div class='col-6 col-sm-4'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-shooting-position'>Shooting position</Text></label>
                                        <input
                                            type='text'
                                            class='form-control'
                                            name='shootingPosition'
                                            value={editObject.shootingPosition}
                                            onInput={this.handleFieldChange}
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div class='col-6 col-sm-4'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-magnum-shooting-time'>Magnum: shooting time (sec)</Text></label>
                                        <input
                                            type='number'
                                            min='0'
                                            class='form-control'
                                            name='magnumShootingTime'
                                            value={editObject.magnumShootingTime}
                                            onInput={this.handleFieldChange}
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div class='col-12 col-sm-8'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-magnum-shooting-position'>Magnum: shooting position</Text></label>
                                        <input
                                            type='text'
                                            class='form-control'
                                            name='magnumShootingPosition'
                                            value={editObject.magnumShootingPosition}
                                            onInput={this.handleFieldChange}
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div class='col-12'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-magnum-notes'>Magnum: notes</Text></label>
                                        <textarea
                                            class='form-control'
                                            rows='2'
                                            name='magnumNotes'
                                            value={editObject.magnumNotes}
                                            onInput={this.handleFieldChange}
                                        />
                                    </div>
                                    <div class='col-12'>
                                        <label class='form-label mb-1'><Text id='convention.standplass-notes'>Notes</Text></label>
                                        <textarea
                                            class='form-control'
                                            rows='3'
                                            name='notes'
                                            value={editObject.notes}
                                            onInput={this.handleFieldChange}
                                        />
                                    </div>
                                </div>
                                <div class='d-flex justify-content-end mt-3'>
                                    <button
                                        type='button'
                                        class='btn btn-primary rounded-pill px-4'
                                        onClick={this.toggleEdit}
                                    >
                                        <i class='fa-solid fa-check me-2' />
                                        <Text id='convention.standplass-edit-done'>Done</Text>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Key facts */}
                    <div class='d-flex flex-wrap justify-content-center gap-2 mb-3'>
                        {shootingTime != null && (
                            <span class='badge bg-body-secondary text-body border rounded-pill px-3 py-2'>
                                <i class='fa-duotone fa-clock me-1' />{shootingTime}s
                            </span>
                        )}
                        {!!shootingPosition && (
                            <span class='badge bg-body-secondary text-body border rounded-pill px-3 py-2'>
                                <i class='fa-duotone fa-person-rifle me-1' />{shootingPosition}
                            </span>
                        )}
                        {magnumFacts && (
                            <span class='badge bg-body-secondary text-body border rounded-pill px-3 py-2'>
                                <i class='fa-duotone fa-bullseye-pointer me-1' />
                                <Text id='convention.course-magnum'>Magnum</Text>: {formatStandMagnumSummary(magnumFacts)}
                            </span>
                        )}
                        {scoreTargets != null && (
                            <span class='badge bg-body-secondary text-body border rounded-pill px-3 py-2'>
                                <i class='fa-duotone fa-bullseye me-1' />{scoreTargets} <Text id='convention.stand-figures'>figures</Text>
                            </span>
                        )}
                        {scoreGroups != null && (
                            <span class='badge bg-body-secondary text-body border rounded-pill px-3 py-2'>
                                <i class='fa-duotone fa-layer-group me-1' />{scoreGroups} <Text id='convention.stand-groups'>target groups</Text>
                            </span>
                        )}
                        {scoreInner != null && (
                            <span class='badge bg-body-secondary text-body border rounded-pill px-3 py-2'>
                                <i class='fa-duotone fa-bullseye-arrow me-1' />{scoreInner} <Text id='convention.stand-zones'>zones</Text>
                            </span>
                        )}
                        {scoreHits != null && (
                            <span class='badge bg-body-secondary text-body border rounded-pill px-3 py-2'>
                                <i class='fa-duotone fa-crosshairs me-1' />Max {scoreHits} <Text id='convention.hits'>Hits</Text>
                            </span>
                        )}
                    </div>

                    {standImage && (
                        <div class='text-center mb-3'>
                            <img
                                src={standImage.s3MediumLink || standImage.s3SmallLink}
                                class='img-fluid rounded'
                                style='max-height: 300px;'
                                alt={`Standplass ${standNum}`}
                            />
                        </div>
                    )}

                    {!!notes && (
                        <div class='alert alert-secondary py-2' style='white-space: pre-wrap;'>
                            <i class='fa-duotone fa-note me-1' />{notes}
                        </div>
                    )}

                    {/* Standplassleder script */}
                    <div class='card'>
                        <div class='card-body'>
                            <h5 class='card-title'>VELKOMMEN TIL STANDPLASS NR. {standNum}</h5>
                            <ul class='mb-0' style='padding-left: 1.2rem;'>
                                {SCRIPT_INTRO.map(item => (
                                    <li key={item.text} class='mb-2'>{item.text}</li>
                                ))}
                                {exerciseLines.map(line => (
                                    <li key={line} class='mb-2 fw-bold'>{line}</li>
                                ))}
                                <li class='mb-2 fw-bold'>ER DET NOEN SPØRSMÅL TIL ØVELSEN?</li>
                                {SCRIPT_COMMANDS.map(item => (
                                    <li key={item.cmd} class='mb-2'>
                                        <span class='fw-bold text-decoration-underline'>{item.cmd}</span> {item.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ConventionStandplassCard;
